import { useState } from "react";
import { ChevronDown, ChevronRight, Mail, Megaphone, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import {
  useCommunications,
  useCommunicationMailLog,
  useCommunicationRecipients,
  usePostCommunication,
  type Communication,
} from "@/features/rfq/api/proceedings";
import { uploadAttachment } from "@/features/rfq/api/requests";
import { uploadErrorMessage } from "@/utils/managedFile";

/**
 * Komunikaty postępowania. `canPost` odróżnia panel GK (można dodać ogłoszenie) od
 * widoku oferenta (tylko czyta). Ta sama lista po obu stronach — oferent widzi
 * dokładnie to, co ogłosiliśmy.
 */

export function CommunicationsPanel({
  proceedingId,
  canPost = false,
}: {
  proceedingId: string;
  canPost?: boolean;
}) {
  const { data } = useCommunications(proceedingId);
  const post = usePostCommunication(proceedingId);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isChange, setIsChange] = useState(false);
  const [toAllEligible, setToAllEligible] = useState(false);
  const [extraRaw, setExtraRaw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Zmiany treści zapytania — tylko przy ogłoszeniu o zmianie.
  const [newDescription, setNewDescription] = useState("");
  const [newDelivery, setNewDelivery] = useState("");
  const [files, setFiles] = useState<{ label: string; file: File | null }[]>([]);
  // Który komunikat ma rozwinięty dziennik dostarczenia.
  const [openedLog, setOpenedLog] = useState<string | null>(null);
  const { data: mailLog } = useCommunicationMailLog(canPost ? openedLog : null);
  // Podgląd odbiorców pobieramy dopiero po otwarciu formularza.
  const { data: recipients } = useCommunicationRecipients(proceedingId, canPost && open);

  const extraEmails = extraRaw
    .split(/[\n,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);

  const resetForm = () => {
    setOpen(false);
    setTitle("");
    setBody("");
    setIsChange(false);
    setToAllEligible(false);
    setExtraRaw("");
    setNewDescription("");
    setNewDelivery("");
    setFiles([]);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) return setError("Podaj tytuł i treść.");
    setError("");
    setBusy(true);
    try {
      const pending = files.filter((f) => f.file);
      const ids = isChange ? await Promise.all(pending.map((f) => uploadAttachment(f.file!))) : [];
      const labels: Record<string, string> = {};
      ids.forEach((id: string, i: number) => (labels[id] = pending[i].label.trim() || pending[i].file!.name));
      await post.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        is_change: isChange,
        to_all_eligible: toAllEligible,
        extra_emails: extraEmails,
        ...(isChange && newDescription.trim() ? { new_description: newDescription.trim() } : {}),
        ...(isChange && newDelivery ? { new_delivery_deadline: newDelivery } : {}),
        ...(ids.length ? { new_attachments: ids, attachment_labels: labels } : {}),
      });
      resetForm();
    } catch (err) {
      setError(uploadErrorMessage(err, "Nie udało się opublikować komunikatu."));
    } finally {
      setBusy(false);
    }
  };

  const list = data ?? [];

  return (
    <div className="bg-white p-4 dark:bg-gray-800">
      {canPost &&
        (open ? (
          <div className="mb-4 space-y-2 border-b border-[var(--color-border)] pb-4">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Tytuł komunikatu"
              className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="Treść — trafi mailem i powiadomieniem do wszystkich oferentów."
              className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
            <label className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={isChange}
                onChange={(e) => setIsChange(e.target.checked)}
              />
              <span>
                Ogłoszenie o <strong>zmianie warunków</strong> — przesunie termin składania
                o co najmniej 3 dni robocze, żeby oferenci zdążyli zareagować.
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={toAllEligible}
                onChange={(e) => setToAllEligible(e.target.checked)}
              />
              <span>
                Wyślij do <strong>całego kręgu odbiorców</strong> (wszystkich objętych
                ogłoszeniem) — domyślnie idzie tylko do firm, które już złożyły ofertę.
              </span>
            </label>

            {isChange && (
              <div className="space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
                <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  Zmiana treści zapytania (opcjonalnie)
                </p>
                <label className="block text-xs text-[var(--color-text-secondary)]">
                  Nowy opis zamówienia
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    rows={2}
                    placeholder="Zostaw puste, by nie zmieniać opisu."
                    className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                  />
                </label>
                <label className="block text-xs text-[var(--color-text-secondary)]">
                  Nowy termin realizacji
                  <DatePicker
                    value={newDelivery}
                    onChange={setNewDelivery}
                    className="mt-1 max-w-[12rem]"
                  />
                </label>
                <div>
                  <p className="mb-1 text-xs text-[var(--color-text-secondary)]">Dodaj załączniki</p>
                  {files.map((row, index) => (
                    <div key={index} className="mb-1 flex flex-wrap items-center gap-2">
                      <input
                        value={row.label}
                        onChange={(e) =>
                          setFiles((prev) =>
                            prev.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)),
                          )
                        }
                        placeholder="Nazwa (np. Nowy przedmiar)"
                        className="min-w-[10rem] flex-1 border border-[var(--color-border)] bg-transparent px-2 py-1 text-sm text-[var(--color-text-primary)]"
                      />
                      <div className="min-w-[12rem] flex-1">
                        <FileDropZone
                          files={row.file ? [row.file] : []}
                          onFilesChange={(fs) =>
                            setFiles((prev) =>
                              prev.map((r, i) => (i === index ? { ...r, file: fs[0] ?? null } : r)),
                            )
                          }
                          label="Plik"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => [...prev, { label: "", file: null }])}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                  >
                    <Plus size={12} /> Dodaj załącznik
                  </button>
                </div>
              </div>
            )}
            {/* Podgląd maila — dokładnie to, co dostanie każdy oferent */}
            <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 text-sm">
              <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Podgląd wiadomości
              </p>
              <p className="mt-1 text-[var(--color-text-primary)]">
                <span className="text-[var(--color-text-muted)]">Temat:</span>{" "}
                Komunikat do postępowania
              </p>
              <p className="mt-1 whitespace-pre-wrap text-[var(--color-text-secondary)]">
                {body || "— treść pojawi się tutaj —"}
              </p>
            </div>

            {/* Do kogo pójdzie — każdy dostaje osobnego maila, bez DW */}
            <div className="border border-[var(--color-border)] p-3">
              <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Odbiorcy ({(recipients ?? []).length} firm) — każdy dostaje osobną wiadomość
              </p>
              <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto text-xs">
                {(recipients ?? []).map((r) => (
                  <li key={r.contractor_id} className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[var(--color-text-primary)]">{r.contractor_name}</span>
                    {r.mail_enabled ? (
                      <span className="text-[var(--color-text-muted)]">
                        {r.emails.length ? r.emails.join(", ") : "brak adresu"}
                      </span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-500">
                        mail wyłączony — nie dostanie
                      </span>
                    )}
                  </li>
                ))}
                {(recipients ?? []).length === 0 && (
                  <li className="text-[var(--color-text-muted)]">
                    Brak firm objętych ogłoszeniem.
                  </li>
                )}
              </ul>
              <label className="mt-2 block">
                <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  Dopisz adresy (oddziel przecinkiem lub nową linią)
                </span>
                <textarea
                  value={extraRaw}
                  onChange={(e) => setExtraRaw(e.target.value)}
                  rows={2}
                  placeholder="np. biuro@firma.pl, kontakt@inna.pl"
                  className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                />
              </label>
              {extraEmails.length > 0 && (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                  + {extraEmails.length} dopisanych adresów, każdy osobno.
                </p>
              )}
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={submit} disabled={busy}>
                {busy ? "Wysyłanie…" : "Opublikuj komunikat"}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" className="mb-4" onClick={() => setOpen(true)}>
            <Plus size={14} className="mr-1.5 inline" /> Dodaj komunikat
          </Button>
        ))}

      {list.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">Brak komunikatów.</p>
      ) : (
        <ul className="space-y-3">
          {list.map((c: Communication) => (
            <li key={c.id} className="border-l-2 border-[var(--color-border)] pl-3">
              <div className="flex flex-wrap items-center gap-2">
                <Megaphone size={13} className="text-[var(--color-text-muted)]" />
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  {c.title}
                </span>
                {c.is_change && <Badge color="yellow">Zmiana warunków</Badge>}
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
                {c.body}
              </p>
              {c.extended_deadline_to && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                  Termin składania przesunięty na{" "}
                  {new Date(c.extended_deadline_to).toLocaleString("pl")}.
                </p>
              )}
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {new Date(c.created_at).toLocaleString("pl")}
                {c.author_name && ` · ${c.author_name}`}
              </p>
              {canPost && (
                <div className="mt-1">
                  <button
                    onClick={() => setOpenedLog(openedLog === c.id ? null : c.id)}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                  >
                    {openedLog === c.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <Mail size={12} /> Kto dostał maila
                  </button>
                  {openedLog === c.id && (
                    <div className="mt-1 space-y-2 border-l border-[var(--color-border)] pl-3">
                      <ul className="space-y-0.5 text-xs">
                        {(mailLog?.rows ?? []).map((row) => (
                          <li key={row.id} className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[var(--color-text-primary)]">
                              {row.contractor_name ?? row.to.join(", ")}
                            </span>
                            <span className="text-[var(--color-text-muted)]">
                              {row.to.join(", ")}
                            </span>
                            <Badge
                              color={
                                row.status === "sent"
                                  ? "green"
                                  : row.status === "failed" ||
                                      row.status === "blocked_foreign_recipient"
                                    ? "red"
                                    : "gray"
                              }
                            >
                              {row.status_label}
                            </Badge>
                          </li>
                        ))}
                        {(mailLog?.rows ?? []).length === 0 && (
                          <li className="text-[var(--color-text-muted)]">Brak wpisów.</li>
                        )}
                      </ul>

                      {(mailLog?.reads ?? []).length > 0 && (
                        <div>
                          <p className="mb-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                            Przeczytali ({mailLog?.read_count}/{mailLog?.expected_count})
                          </p>
                          <ul className="space-y-0.5 text-xs">
                            {(mailLog?.reads ?? []).map((r, i) => (
                              <li key={i} className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[var(--color-text-primary)]">
                                  {r.user_name}
                                </span>
                                {r.contractor_name && (
                                  <span className="text-[var(--color-text-muted)]">
                                    {r.contractor_name}
                                  </span>
                                )}
                                {r.read_at ? (
                                  <Badge color="green">
                                    {new Date(r.read_at).toLocaleString("pl")}
                                  </Badge>
                                ) : (
                                  <Badge color="yellow">nie przeczytał</Badge>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
